import { useTranslation } from 'react-i18next';
import type { Profile } from '@/auth/OperatorProvider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { formatDate } from '@/lib/intl';

type Membership = Profile['memberships'][number];

/**
 * Read-only rendering of the caller's own Department/Group memberships (FR-SP-2), unioned across
 * every one of the caller's own eligible organizations (SL-0022, Resolution 3) — renders only the
 * array `GET /v1/profile` already returns; issues no fetch of its own (Resolution 5). No control
 * here adds, removes, or otherwise edits a membership.
 */
export function MembershipsSection({ memberships }: { memberships: readonly Membership[] }) {
  const { t } = useTranslation('profile');
  const collectiveTypeLabel = (type: Membership['collectiveType']): string =>
    type === 'department'
      ? t('memberships.collectiveType.department')
      : t('memberships.collectiveType.group');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="card-label">{t('memberships.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={[
            {
              id: 'collectiveType',
              header: t('memberships.typeHeader'),
              cell: ({ row }: { row: { original: Membership } }) =>
                collectiveTypeLabel(row.original.collectiveType),
            },
            {
              id: 'collectiveId',
              header: t('memberships.collectiveIdHeader'),
              cell: ({ row }: { row: { original: Membership } }) => (
                <code>{row.original.collectiveId}</code>
              ),
            },
            {
              id: 'organizationId',
              header: t('memberships.organizationIdHeader'),
              cell: ({ row }: { row: { original: Membership } }) => (
                <code>{row.original.organizationId}</code>
              ),
            },
            {
              id: 'createdAt',
              header: t('memberships.sinceHeader'),
              cell: ({ row }: { row: { original: Membership } }) =>
                formatDate(row.original.createdAt),
            },
          ]}
          data={[...memberships]}
          emptyMessage={t('memberships.emptyMessage')}
        />
      </CardContent>
    </Card>
  );
}
